import { FIELD_BY_HEADING, readYesNo, flattenPersonName, flatten } from './WarehouseFields';

// Working out what an import file would do, without doing any of it.
//
// Kept apart from the screen on purpose: this is the part that can quietly get
// a thousand rows wrong, so it is plain functions over plain data that can be
// run against a real export and checked.
//
// Two things about the Claimsoft warehouse export in particular:
//
//   It repeats a warehouse once per bin. SAT-MAIN appears 340 times in a 931
//   row file. Repeats are counted and skipped, not treated as errors.
//
//   "Primary Warehouse" points at another warehouse in the same file, which may not
//   exist yet when its row is read, so it is carried as a name and linked in a
//   second pass once everything has been created.

// The prefix before the first dash says which region something is in. The
// database keeps this in region_prefixes; this is the same list, used to fill
// the region in when a file does not say. Anything not listed is left blank
// rather than guessed at.
export const PREFIX_REGION = {
  AUS: 'Austin', AUSS: 'Austin', CCH: 'Corpus', DFW: 'Dallas',
  HOU: 'Houston', HOUX: 'Houston', LOU: 'Louisiana', OKL: 'Oklahoma',
  SAT: 'San Antonio', SPU: 'San Antonio', ETX: 'Tyler',
};

export function buildIndex({ regions = [], referrals = [], people = [], warehouses = [] }) {
  return {
    region: new Map(regions.map(r => [flatten(r.name), r.id])),
    referral: new Map([
      ...referrals.map(r => [flatten(r.name), r.id]),
      ...referrals.filter(r => r.display_name).map(r => [flatten(r.display_name), r.id]),
    ]),
    personByEmail: new Map(people.filter(p => p.email).map(p => [flatten(p.email), p.id])),
    personByName:  new Map(people.map(p => [flattenPersonName(p.full_name), p.id])),
    byNumber: new Map(warehouses.filter(w => w.warehouse_number).map(w => [flatten(w.warehouse_number), w])),
    byName:   new Map(warehouses.map(w => [flatten(w.name), w])),
    warehouses,
  };
}

export function findPerson(index, raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  return index.personByEmail.get(flatten(s))
      ?? index.personByName.get(flattenPersonName(s))
      ?? null;
}

export function regionFromName(index, name) {
  const dash = String(name).indexOf('-');
  if (dash <= 0) return null;
  const prefix = flatten(String(name).slice(0, dash)).replace(/^DNU/, '');
  const want = PREFIX_REGION[prefix];
  return want ? index.region.get(flatten(want)) ?? null : null;
}

// Which headings in this file map to which field. One heading per field; if a
// file carries two headings meaning the same thing, the first wins.
export function readHeaders(headers) {
  const used = [];
  const claimed = new Set();
  const clean = headers.map(h => (h || '').trim()).filter(Boolean);
  clean.forEach(h => {
    const f = FIELD_BY_HEADING[h.toLowerCase()];
    if (f && !claimed.has(f.key)) { claimed.add(f.key); used.push({ heading: h, field: f }); }
  });
  return {
    used,
    ignored: clean.filter(h => !FIELD_BY_HEADING[h.toLowerCase()]),
    nameHeader: used.find(u => u.field.key === 'name')?.heading || null,
  };
}

export function buildPlan(data, headers, index) {
  const { used, ignored, nameHeader } = readHeaders(headers);
  if (!nameHeader) return { error: 'no-name-column' };

  const numberHeading = used.find(u => u.field.key === 'warehouse_number')?.heading;
  const nameById = new Map(index.warehouses.map(w => [w.id, w.name]));

  const creates = [], updates = [], unchanged = [], problems = [];
  const seen = new Map();
  let repeats = 0;

  data.forEach((raw, i) => {
    const line = i + 2;                     // +1 for the header, +1 because people count from 1
    const cellOf = h => String(raw[h] ?? '').trim();

    const name = cellOf(nameHeader);
    if (!name) return;                      // blank row at the end of a spreadsheet

    const number = numberHeading ? cellOf(numberHeading) : '';
    const key = number ? 'n:' + flatten(number) : 'w:' + flatten(name);
    if (seen.has(key)) { repeats++; return; }
    seen.set(key, line);

    const existing = (number && index.byNumber.get(flatten(number))) || index.byName.get(flatten(name)) || null;

    const values = {};
    let assigned = null;                    // null means the file says nothing about this
    let hubName  = null;

    used.forEach(({ heading, field: f }) => {
      const cell = cellOf(heading);

      if (f.kind === 'warehouse') { if (cell) hubName = cell; return; }

      if (f.kind === 'people') {
        if (!cell) return;
        const ids = [];
        cell.split(/[;,]/).map(s => s.trim()).filter(Boolean).forEach(who => {
          const id = findPerson(index, who);
          if (id) ids.push(id);
          else problems.push({ line, field: f.column, value: who, note: 'no matching person' });
        });
        assigned = [...new Set(ids)];
        return;
      }

      if (f.kind === 'yesno') {
        const v = readYesNo(cell);
        if (v !== null) values[f.key] = v;
        return;
      }

      if (f.kind === 'person') {
        values.warehouse_manager = cell || null;   // keep the raw name either way
        if (!cell) { values.manager_id = null; return; }
        const id = findPerson(index, cell);
        if (id) values.manager_id = id;
        else problems.push({ line, field: f.column, value: cell, note: 'no matching person' });
        return;
      }

      if (f.kind === 'region' || f.kind === 'referral') {
        if (!cell) return;                          // blank means leave it alone
        const id = index[f.kind].get(flatten(cell));
        if (id) values[f.key] = id;
        else problems.push({
          line, field: f.column, value: cell,
          note: f.kind === 'referral' ? 'no referral source with that name' : 'not a region',
        });
        return;
      }

      values[f.key] = cell === '' ? null : cell;
    });

    values.name = name;

    // No region given: work it out from the name, the way the database does.
    if (values.region_id == null && !(existing && existing.region_id)) {
      const guess = regionFromName(index, name);
      if (guess) values.region_id = guess;
    }

    if (!existing) {
      creates.push({ line, name, values, assigned, hubName });
    } else {
      const changes = Object.keys(values).filter(
        k => String(existing[k] ?? '') !== String(values[k] ?? ''),
      );
      const hubChanged = !!hubName
        && flatten(nameById.get(existing.primary_warehouse_id) || '') !== flatten(hubName);
      if (!changes.length && assigned === null && !hubChanged) unchanged.push({ line, name });
      else updates.push({ line, name, id: existing.id, values, changes, assigned, hubName });
    }
  });

  return { creates, updates, unchanged, problems, ignored, repeats, rowCount: data.length };
}

// The same misspelled name in four hundred rows is one problem, not four
// hundred. Group by what was not found and count.
export function summarise(problems) {
  const by = new Map();
  problems.forEach(p => {
    const k = p.field + '|' + p.value;
    const hit = by.get(k);
    if (hit) hit.count++;
    else by.set(k, { ...p, count: 1 });
  });
  return [...by.values()].sort((a, b) => b.count - a.count);
}
